import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { EventCardSkeleton } from '@/components/ui/skeleton';
import { NoEventsState, NetworkErrorState } from '@/components/EmptyState';
import Navbar from '@/components/Navbar';
import { 
  Ticket, 
  Calendar, 
  Clock, 
  MapPin, 
  Search,
  ChevronRight,
  Sparkles,
  Trophy,
  Music,
  PartyPopper,
  Shield,
  Smartphone,
  Zap,
  Star,
  ArrowRight
} from 'lucide-react';
import { GAM_TICKET_LOGO, HERO_BG, CONTACT_EMAIL, CONTACT_PHONE } from '@/config/assets';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 15 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 }
  }
};

// Compact Mobile Event Card
const MobileEventCard = ({ event, formatDate }) => {
  const getEventTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'football': return 'bg-emerald-500/90';
      case 'concert': return 'bg-purple-500/90';
      case 'festival': return 'bg-amber-500/90';
      default: return 'bg-blue-500/90';
    }
  };

  return (
    <Link to={`/event/${event.id}`} className="block flex-shrink-0 w-[160px] sm:w-[200px]">
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-b from-white/[0.06] to-white/[0.02] border border-white/[0.06] active:scale-[0.98] transition-transform">
        {/* Image */}
        <div className="relative aspect-[3/4] overflow-hidden">
          {event.flyer_url ? (
            <img 
              src={event.flyer_url} 
              alt={event.name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#0055FF]/30 to-purple-600/30 flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-white/20" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          
          {/* Badge */}
          <Badge className={`absolute top-2 left-2 ${getEventTypeColor(event.event_type)} text-[10px] font-medium px-2 py-0.5 rounded-md`}>
            {event.event_type || 'Event'}
          </Badge>

          {/* Price */}
          <div className="absolute bottom-2 left-2 right-2">
            <p className="font-bold text-white text-sm truncate">{event.name}</p>
            <div className="flex items-center justify-between mt-1">
              <span className="text-[10px] text-gray-400">{formatDate(event.date)}</span>
              <span className="text-xs font-bold text-[#0055FF]">D{event.ticket_price}</span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

// Desktop Event Card
const DesktopEventCard = ({ event, formatDate, index }) => {
  const getEventTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'football': return 'bg-emerald-500/90 text-white';
      case 'concert': return 'bg-purple-500/90 text-white';
      case 'festival': return 'bg-amber-500/90 text-white';
      default: return 'bg-blue-500/90 text-white';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
    >
      <Link to={`/event/${event.id}`} className="group block">
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-b from-white/[0.06] to-white/[0.02] border border-white/[0.06] hover:border-white/15 transition-all duration-300 hover:shadow-xl hover:shadow-[#0055FF]/10 hover:-translate-y-1">
          <div className="relative aspect-[4/3] overflow-hidden">
            {event.flyer_url ? (
              <img 
                src={event.flyer_url} 
                alt={event.name}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#0055FF]/30 to-purple-600/30 flex items-center justify-center">
                <Sparkles className="w-10 h-10 text-white/20" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            
            <Badge className={`absolute top-2.5 left-2.5 ${getEventTypeColor(event.event_type)} backdrop-blur-sm text-[11px] font-medium px-2 py-0.5 rounded-md`}>
              {event.event_type || 'Event'}
            </Badge>

            <div className="absolute bottom-2.5 right-2.5 bg-black/50 backdrop-blur px-2 py-1 rounded-md border border-white/10">
              <span className="text-[10px] text-gray-400">From </span>
              <span className="text-white font-bold text-sm">D{event.ticket_price}</span>
            </div>
          </div>

          <div className="p-3">
            <h3 className="font-semibold text-white text-sm mb-1.5 line-clamp-1 group-hover:text-[#0055FF] transition-colors">
              {event.name}
            </h3>
            <div className="flex items-center gap-2 text-[11px] text-gray-400">
              <Calendar className="w-3 h-3 text-[#0055FF]" />
              <span>{formatDate(event.date)}</span>
            </div>
            <div className="flex items-center gap-2 mt-1 text-[11px] text-gray-500">
              <MapPin className="w-3 h-3" />
              <span className="truncate">{event.venue}</span>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

// Compact Mobile Fixture Card
const MobileFixtureCard = ({ fixture, onClick }) => (
  <div
    onClick={onClick}
    className="flex-shrink-0 w-[200px] p-3 rounded-xl bg-gradient-to-b from-white/[0.05] to-white/[0.02] border border-white/[0.06] active:scale-[0.98] transition-transform cursor-pointer"
  >
    {fixture.is_live && (
      <div className="flex justify-end mb-1">
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase bg-red-500 text-white rounded-full">
          <span className="w-1 h-1 bg-white rounded-full animate-pulse"></span>
          LIVE
        </span>
      </div>
    )}
    
    <div className="text-[10px] text-gray-500 mb-2">{fixture.date} • {fixture.time}</div>
    
    <div className="flex items-center justify-between gap-1">
      <div className="flex-1 text-center">
        <div className="w-8 h-8 mx-auto mb-1 rounded-full bg-white/10 flex items-center justify-center">
          <Trophy className="w-4 h-4 text-gray-500" />
        </div>
        <span className="font-medium text-white text-[11px] block truncate">{fixture.home_team}</span>
      </div>
      <span className="text-[10px] text-gray-600 font-medium">VS</span>
      <div className="flex-1 text-center">
        <div className="w-8 h-8 mx-auto mb-1 rounded-full bg-white/10 flex items-center justify-center">
          <Trophy className="w-4 h-4 text-gray-500" />
        </div>
        <span className="font-medium text-white text-[11px] block truncate">{fixture.away_team}</span>
      </div>
    </div>
    
    <div className="mt-2 pt-2 border-t border-white/5 flex items-center justify-between">
      <span className="text-[10px] text-gray-500 truncate max-w-[100px]">{fixture.venue}</span>
      <span className="text-[11px] font-bold text-[#0055FF]">D{fixture.ticket_price || 100}</span>
    </div>
  </div>
);

// Desktop Fixture Card
const DesktopFixtureCard = ({ fixture, onClick }) => (
  <motion.div
    whileHover={{ scale: 1.01 }}
    whileTap={{ scale: 0.99 }}
    onClick={onClick}
    className="group p-4 rounded-xl bg-gradient-to-b from-white/[0.05] to-white/[0.02] border border-white/[0.06] hover:border-[#0055FF]/30 cursor-pointer transition-all duration-200 hover:shadow-lg hover:shadow-[#0055FF]/5"
  >
    {fixture.is_live && (
      <div className="absolute top-3 right-3">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase bg-red-500 text-white rounded-full">
          <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
          LIVE
        </span>
      </div>
    )}
    
    <div className="text-xs text-gray-500 mb-3">{fixture.date} • {fixture.time}</div>
    
    <div className="flex items-center justify-between gap-2">
      <div className="flex-1 text-center">
        <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-white/10 flex items-center justify-center">
          <Trophy className="w-5 h-5 text-gray-400" />
        </div>
        <span className="font-semibold text-white text-sm block truncate">{fixture.home_team}</span>
      </div>
      <span className="text-xs font-bold text-gray-500 bg-white/5 px-2 py-1 rounded">VS</span>
      <div className="flex-1 text-center">
        <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-white/10 flex items-center justify-center">
          <Trophy className="w-5 h-5 text-gray-400" />
        </div>
        <span className="font-semibold text-white text-sm block truncate">{fixture.away_team}</span>
      </div>
    </div>
    
    <div className="flex items-center justify-center gap-1.5 mt-3 text-[11px] text-gray-500">
      <MapPin className="w-3 h-3" />
      <span className="truncate">{fixture.venue}</span>
    </div>

    <div className="mt-3 pt-3 border-t border-white/5 text-center">
      <span className="text-xs text-[#0055FF] font-medium">From D{fixture.ticket_price || 100} →</span>
    </div>
  </motion.div>
);

// Horizontal Scroll Container
const HorizontalScroll = ({ children, className = '' }) => (
  <div className={`flex gap-3 overflow-x-auto scrollbar-hide pb-2 -mx-4 px-4 snap-x snap-mandatory ${className}`}>
    {children}
  </div>
);

export default function LandingPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [eventsRes, fixturesRes, tournamentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/events/public`),
        axios.get(`${API_URL}/api/fixtures/latest?limit=8`),
        axios.get(`${API_URL}/api/tournaments?active_only=true`)
      ]);
      
      setEvents(eventsRes.data);
      setFixtures(fixturesRes.data);
      setZones(tournamentsRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/events?search=${encodeURIComponent(searchQuery)}`);
    } else {
      navigate('/events');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short'
    });
  };

  const featuredEvents = events.slice(0, 8);

  return (
    <div className="min-h-screen bg-[#0a0a0a] overflow-x-hidden">
      <Navbar />

      {/* Hero Section - Mobile Optimized */}
      <section className="relative min-h-[40vh] sm:min-h-[45vh] lg:min-h-[50vh] flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${HERO_BG})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/90 via-[#0a0a0a]/70 to-[#0a0a0a]" />
          {/* Glow - Hidden on mobile for performance */}
          <div className="hidden sm:block absolute top-1/4 left-1/4 w-64 lg:w-96 h-64 lg:h-96 bg-[#0055FF]/15 rounded-full blur-[100px]" />
          <div className="hidden sm:block absolute bottom-1/4 right-1/4 w-64 lg:w-96 h-64 lg:h-96 bg-purple-600/10 rounded-full blur-[100px]" />
        </div>

        {/* Content */}
        <motion.div 
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="relative z-10 w-full max-w-4xl mx-auto px-4 text-center py-8 sm:py-10"
        >
          <motion.p 
            variants={fadeInUp}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/[0.08] text-[11px] sm:text-sm text-gray-400 mb-4 sm:mb-5"
          >
            <Zap className="w-3 h-3 sm:w-4 sm:h-4 text-[#0055FF]" />
            <span>The Gambia's Premier Ticketing</span>
          </motion.p>
          
          <motion.h1 
            variants={fadeInUp}
            className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4 leading-tight tracking-tight"
          >
            Your Gateway to
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#0055FF] via-blue-400 to-purple-500">
              Unforgettable Moments
            </span>
          </motion.h1>

          <motion.p 
            variants={fadeInUp}
            className="text-gray-400 text-sm sm:text-base mb-5 sm:mb-6 max-w-lg mx-auto px-4"
          >
            Book tickets for football, concerts, and festivals.
          </motion.p>

          {/* Search Box */}
          <motion.form 
            variants={fadeInUp}
            onSubmit={handleSearch} 
            className="max-w-md mx-auto px-2"
          >
            <div className="relative bg-white/[0.05] backdrop-blur-xl rounded-xl border border-white/[0.08] p-1 flex gap-1.5">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  type="text"
                  placeholder="Search events..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-10 sm:h-11 border-0 bg-transparent text-white placeholder-gray-500 text-sm focus:ring-0 focus:outline-none"
                />
              </div>
              <Button 
                type="submit" 
                className="h-10 sm:h-11 px-4 bg-[#0055FF] hover:bg-[#0066FF] text-white rounded-lg text-sm font-medium"
              >
                <Search className="w-4 h-4" />
              </Button>
            </div>
          </motion.form>

          {/* Stats */}
          <motion.div 
            variants={fadeInUp}
            className="flex justify-center gap-6 sm:gap-12 mt-6 sm:mt-8"
          >
            {[
              { value: `${events.length}+`, label: 'Events' },
              { value: '5K+', label: 'Sold' },
              { value: '100%', label: 'Secure' }
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-lg sm:text-2xl font-bold text-white">{stat.value}</p>
                <p className="text-[10px] sm:text-xs text-gray-500">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* Category Pills - Compact */}
      <section className="py-4 sm:py-6 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-center gap-2 sm:gap-3">
            {[
              { to: '/football', icon: Trophy, label: 'Football', color: 'text-emerald-400' },
              { to: '/concerts', icon: Music, label: 'Concerts', color: 'text-purple-400' },
              { to: '/festivals', icon: PartyPopper, label: 'Festivals', color: 'text-amber-400' }
            ].map((item, i) => (
              <Link 
                key={i}
                to={item.to}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-full bg-white/[0.03] border border-white/[0.06] text-white text-xs sm:text-sm font-medium transition-all active:scale-95"
              >
                <item.icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${item.color}`} />
                <span>{item.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Fixtures */}
      {fixtures.length > 0 && (
        <section className="py-6 sm:py-10 bg-[#0a0a0a]">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-4 sm:mb-6 px-4">
              <div>
                <h2 className="text-base sm:text-xl font-bold text-white">Latest Fixtures</h2>
                <p className="text-[11px] sm:text-sm text-gray-500">Upcoming matches</p>
              </div>
              <Link to="/football" className="text-xs sm:text-sm text-[#0055FF] flex items-center gap-0.5">
                View All <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            {/* Mobile: Horizontal Scroll */}
            <div className="sm:hidden">
              <HorizontalScroll>
                {fixtures.slice(0, 6).map((fixture) => (
                  <MobileFixtureCard 
                    key={fixture.id} 
                    fixture={fixture}
                    onClick={() => navigate(`/fixture/${fixture.id}`)}
                  />
                ))}
              </HorizontalScroll>
            </div>

            {/* Desktop: Grid */}
            <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-4 px-4">
              {fixtures.slice(0, 4).map((fixture, index) => (
                <DesktopFixtureCard 
                  key={fixture.id} 
                  fixture={fixture}
                  onClick={() => navigate(`/fixture/${fixture.id}`)}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Events */}
      <section className="py-6 sm:py-10 bg-gradient-to-b from-[#0a0a0a] to-[#0c0c0c]">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4 sm:mb-6 px-4">
            <div>
              <h2 className="text-base sm:text-xl font-bold text-white">Featured Events</h2>
              <p className="text-[11px] sm:text-sm text-gray-500">Don't miss out</p>
            </div>
            <Link to="/events" className="text-xs sm:text-sm text-[#0055FF] flex items-center gap-0.5">
              View All <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {loading && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 px-4">
              {[1, 2, 3, 4].map((i) => (
                <EventCardSkeleton key={i} />
              ))}
            </div>
          )}

          {!loading && error && (
            <div className="px-4">
              <NetworkErrorState onRetry={fetchData} />
            </div>
          )}

          {!loading && !error && events.length === 0 && (
            <div className="px-4">
              <NoEventsState onRefresh={fetchData} />
            </div>
          )}

          {!loading && !error && featuredEvents.length > 0 && (
            <>
              {/* Mobile: Horizontal Scroll */}
              <div className="sm:hidden">
                <HorizontalScroll>
                  {featuredEvents.map((event) => (
                    <MobileEventCard 
                      key={event.id} 
                      event={event} 
                      formatDate={formatDate}
                    />
                  ))}
                </HorizontalScroll>
              </div>

              {/* Desktop: Grid */}
              <div className="hidden sm:grid sm:grid-cols-3 lg:grid-cols-4 gap-4 px-4">
                {featuredEvents.map((event, index) => (
                  <DesktopEventCard 
                    key={event.id} 
                    event={event} 
                    formatDate={formatDate}
                    index={index}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Football Zones - Compact */}
      {zones.length > 0 && (
        <section className="py-6 sm:py-10 bg-[#0c0c0c]">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-5 sm:mb-8 px-4">
              <h2 className="text-base sm:text-xl font-bold text-white mb-1">Football Zones</h2>
              <p className="text-[11px] sm:text-sm text-gray-500">Browse by region</p>
            </div>

            {/* Mobile: Horizontal Scroll */}
            <div className="sm:hidden">
              <HorizontalScroll>
                {zones.slice(0, 6).map((zone) => (
                  <Link 
                    key={zone.id}
                    to={`/football/${zone.id}`}
                    className="flex-shrink-0 w-[100px] p-3 rounded-xl bg-gradient-to-b from-white/[0.04] to-white/[0.01] border border-white/[0.06] text-center active:scale-95 transition-transform"
                  >
                    <div className="text-2xl mb-1.5">{zone.flag_icon || '🇬🇲'}</div>
                    <h3 className="font-medium text-white text-xs truncate">{zone.name}</h3>
                    <p className="text-[9px] text-gray-500 mt-0.5">{zone.fixtures_count || 0} fixtures</p>
                  </Link>
                ))}
              </HorizontalScroll>
            </div>

            {/* Desktop: Grid */}
            <div className="hidden sm:grid sm:grid-cols-3 lg:grid-cols-6 gap-3 px-4">
              {zones.slice(0, 6).map((zone) => (
                <Link 
                  key={zone.id}
                  to={`/football/${zone.id}`}
                  className="group p-4 rounded-xl bg-gradient-to-b from-white/[0.04] to-white/[0.01] border border-white/[0.06] hover:border-emerald-500/30 text-center transition-all duration-200"
                >
                  <div className="text-2xl sm:text-3xl mb-2">{zone.flag_icon || '🇬🇲'}</div>
                  <h3 className="font-semibold text-white text-sm group-hover:text-emerald-400 transition-colors">{zone.name}</h3>
                  <p className="text-[10px] text-gray-500 mt-1">{zone.fixtures_count || 0} fixtures</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why Choose Us - Compact */}
      <section className="py-8 sm:py-12 bg-[#0a0a0a]">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-base sm:text-xl font-bold text-white mb-1">Why Gam Ticket?</h2>
            <p className="text-[11px] sm:text-sm text-gray-500">Trusted ticketing platform</p>
          </div>
          
          <div className="grid grid-cols-3 gap-3 sm:gap-5">
            {[
              { icon: Shield, title: 'Secure', desc: 'QR tickets', color: 'text-blue-400', bg: 'bg-blue-500/10' },
              { icon: Smartphone, title: 'Mobile', desc: 'ModemPay', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
              { icon: Star, title: 'Venues', desc: 'All stadiums', color: 'text-purple-400', bg: 'bg-purple-500/10' }
            ].map((feature, i) => (
              <div key={i} className="text-center p-3 sm:p-5 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                <div className={`w-10 h-10 sm:w-12 sm:h-12 ${feature.bg} rounded-xl flex items-center justify-center mx-auto mb-2 sm:mb-3`}>
                  <feature.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${feature.color}`} />
                </div>
                <h3 className="font-semibold text-white text-xs sm:text-sm mb-0.5">{feature.title}</h3>
                <p className="text-gray-500 text-[10px] sm:text-xs">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer - Compact */}
      <footer className="bg-[#080808] text-white py-8 sm:py-10 border-t border-white/[0.05]">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between gap-6 sm:gap-8 mb-6">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg overflow-hidden bg-gradient-to-br from-[#0055FF] to-purple-600">
                <img src={GAM_TICKET_LOGO} alt="Gam Ticket" className="w-full h-full object-cover" />
              </div>
              <div>
                <span className="font-bold text-sm">Gam</span>
                <span className="font-bold text-sm text-[#0055FF]">Ticket</span>
              </div>
            </div>

            {/* Links */}
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <Link to="/football" className="hover:text-white transition-colors">Football</Link>
              <Link to="/concerts" className="hover:text-white transition-colors">Concerts</Link>
              <Link to="/festivals" className="hover:text-white transition-colors">Festivals</Link>
              <Link to="/events" className="hover:text-white transition-colors">Events</Link>
            </div>

            {/* Contact */}
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-white transition-colors">{CONTACT_EMAIL}</a>
            </div>
          </div>

          {/* Legal Links */}
          <div className="flex justify-center gap-6 mb-5 text-xs text-gray-500">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
          </div>

          <div className="border-t border-white/[0.05] pt-5 text-center text-[10px] sm:text-xs text-gray-600">
            © 2026 Gam Ticket. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Custom scrollbar hide styles */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}
